"use client";

import { useState,useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TRYON_SERVER_URL } from "../config";
import { selectAccessToken, selectUserData } from "../selectors";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "../Utils/UserUtils";
import { setUserData } from "../store/AuthActions";
import Spinner from '../components/Spinner';

const PROMPT_CREDITS = 1;

const GenerateFashionModelPromptBuilder = ({
  setShowGenerateFashionModelPromptBuilderModal,
  onSelectPrompt,
  showGenerateFashionModelPromptBuilderModalRef,
  isModal = false,
}) => {
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [customAge, setCustomAge] = useState("");

  const [selectedEthnicity, setSelectedEthnicity] = useState("");
  const [selectedComplexion, setSelectedComplexion] = useState("");
  const [selectedHairStyle, setSelectedHairStyle] = useState("");
  const [selectedFaceShape, setSelectedFaceShape] = useState("");
  const [selectedEyeType, setSelectedEyeType] = useState("");
  const [selectedEyeColor, setSelectedEyeColor] = useState("");
  const [selectedExpression, setSelectedExpression] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const accessToken = useSelector(selectAccessToken);
  const userData = JSON.parse(useSelector(selectUserData) || "null");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
     

  const ageOptions = [
    "Infant (0-2 years)",
    "Toddler (3-5 years)", 
    "Child (6-12 years)",
    "Teen (13-17 years)",
    "Young Adult (18-25 years)",
    "Adult (26-35 years)",
    "Mature Adult (36-50 years)",
    "Senior (51+ years)",
    "Custom"
  ];
  const filteredAgeOptions =
    selectedAge === "Custom"
      ? ageOptions.filter((opt) => opt !== "Custom")
      : ageOptions;

  const hairStyleOptions = {
    Male: ["Short", "Short curly", "Very Long Wave", "Coily"],
    Female: [
      "Long straight",
      "Wavy",
      "Shoulder Length Hair with Bangs",
      "Shoulder Length Straight",
      "Long curly",
      "Very Long Wave",
      "Coily",
    ],
    Other: [
      "Long straight",
      "Wavy",
      "Short",
      "Short curly",
      "Long curly",
      "Shoulder Length Hair with Bangs",
      "Shoulder Length Straight",
      "Very Long Wave",
      "Coily",
    ],
  };

  const ethnicityOptions = [
    "Asian",
    "African", 
    "African American",
    "Caucasian",
    "Hispanic/Latino",
    "Middle Eastern",
    "Pacific Islander",
    "Mixed Race",
    "Other"
  ];
  const complexionOptions = ["Fair", "Light", "Medium", "Dark", "Deep"];
  const eyeTypeOptions = ["Natural", "Expressive", "Intense", "Soft", "Bold"];
  const eyeColorOptions = ["Brown", "Blue", "Green", "Hazel", "Gray", "Black"];
  const expressionOptions = ["Neutral", "Happy", "Confident", "Serious", "Playful"];

  const faceShapeOptions = {
    Male: ["Round", "Symmetrical"],
    Female: ["Heart-shaped", "Symmetrical", "Round"],
    Other: ["Round", "Heart-shaped", "Asymmetrical", "Symmetrical"],
  };

  const getOptions = (map) => map[selectedGender] || map["Other"];

  const renderButton = (value, selected, setter) => (
    <button
      key={value}
      type="button"
      onClick={() => setter(value)}
      className={`whitespace-nowrap rounded-full bg-white px-3 py-1.5 font-Mulish text-xs ${
        selected === value
          ? "border-2 border-primary-500 font-semibold text-secondary"
          : "border border-hairline text-graphite hover:border-primary-200"
      }`}
    >
      {value}
    </button>
  );

  const handlePromptSubmit = async () => {
    setError("");

    if (!accessToken) {
      setError("Access token missing. Please login again.");
      return;
    }

    if (selectedGender === "") {
      setError("Please select a gender.");
      return;
    }
    if (selectedAge === "" || (selectedAge === "Custom" && customAge === "")) {
      setError("Please select an age or enter a custom age.");
      return;
    }

    const balance = Number(userData?.credits);
    if (!Number.isFinite(balance) || balance < PROMPT_CREDITS) {
      navigate(`/credits?buy_credits=true&num_of_credits=${PROMPT_CREDITS}`);
      return;
    }

    const parameters = createParameters();
    setLoading(true);

    try {
      const response = await fetch(
        `${TRYON_SERVER_URL}/api/v1/prompt/generate/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            parameters: parameters,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        const apiPrompt =
          data?.prompt ||
          data?.choices?.[0]?.message?.content ||
          data?.choices?.[0]?.text ||
          "";
        setError("");
        const user = await fetchUser(accessToken);
        if (user.status === "success") {
          dispatch(setUserData(JSON.stringify(user.data)));
        }

        if (onSelectPrompt) {
          onSelectPrompt(apiPrompt);
          setShowGenerateFashionModelPromptBuilderModal(false);
        }
      } else if (data?.error === "Insufficient credits") {
        navigate(`/credits?buy_credits=true&num_of_credits=${PROMPT_CREDITS}`);
      } else {
        setError(
          data?.detail || data?.error || "The prompt could not be written. Try again."
        );
      }
    } catch (error) {
      console.error("Network or Auth Error:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  /*
  create parameters object with the following keys:
  gender: str,
  age: str,
  ethnicity: str,
  complexion: str,
  hair_style: str,
  face_shape: str,
  eye_type: str,
  eye_color: str,
  expression: str,
  */
  const createParameters = () => {
    const parameters = {
      gender: selectedGender,
      age: selectedAge === "Custom" ? customAge : selectedAge,
    };
    if (selectedEthnicity !== "") {
      parameters.ethnicity = selectedEthnicity;
    }
    if (selectedComplexion !== "") {
      parameters.complexion = selectedComplexion;
    }
    if (selectedHairStyle !== "") {
      parameters.hair_style = selectedHairStyle;
    }
    if (selectedFaceShape !== "") {
      parameters.face_shape = selectedFaceShape;
    }
    if (selectedEyeType !== "") {
      parameters.eye_type = selectedEyeType;
    }
    if (selectedEyeColor !== "") {
      parameters.eye_color = selectedEyeColor;
    }
    if (selectedExpression !== "") {
      parameters.expression = selectedExpression;
    }
    return parameters;
  };

  const isFormComplete = selectedGender && selectedAge;

  return (
    <div className="relative" ref={showGenerateFashionModelPromptBuilderModalRef}>
      <div className={` ${
      isModal
        ? "flex w-[800px] max-w-full flex-col items-center justify-center rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)]"
        : "absolute bottom-full right-1/2 z-[100] mb-8 w-[800px] max-w-[calc(100vw-2rem)] translate-x-1/2 overflow-x-auto rounded-2xl bg-white px-5 py-5 shadow-[0_0_0_1px_var(--hairline)]"
    }`}>
        <div className="flex w-full flex-col gap-5">
          <div className="border-b border-hairline px-12 pb-2 text-center">
            <h1 className="font-Mulish text-xl font-semibold tracking-tight text-secondary">
              Fashion Model Prompt Builder
            </h1>
            <p className="mt-1 font-Mulish text-xs text-graphite">
              Customize your fashion model generation.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowGenerateFashionModelPromptBuilderModal(false)}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white text-secondary shadow-[0_0_0_1px_var(--hairline)]"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex flex-col gap-2.5 max-h-[60vh] overflow-y-auto scroll-smooth">
            <div className="space-y-2.5">
              <h3 className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Gender
              </h3>
              <div className="grid grid-cols-3 gap-2.5">
                {["Male", "Female", "Other"].map((gender) => (
                  <button
                    key={gender}
                    onClick={() => {
                      setSelectedGender(gender);
                      setSelectedHairStyle("");
                      setSelectedFaceShape("");
                    }}
                    className={`rounded-xl bg-white px-3 py-2.5 font-Mulish text-sm ${
                      selectedGender === gender
                        ? "border-2 border-primary-500 font-semibold text-secondary"
                        : "border border-hairline text-graphite hover:border-primary-200"
                    }`}
                  >
                    {gender}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary">Age</label>
              <div className="flex flex-wrap items-center gap-2.5">
                {filteredAgeOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => setSelectedAge(option)}
                    className={`whitespace-nowrap rounded-full bg-white px-3 py-1.5 font-Mulish text-xs ${
                      selectedAge === option
                        ? "border-2 border-primary-500 font-semibold text-secondary"
                        : "border border-hairline text-graphite hover:border-primary-200"
                    }`}
                  >
                    {option}
                  </button>
                ))}
                {selectedAge === "Custom" && (
                  <input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="Enter age"
                    value={customAge}
                    onChange={(e) => setCustomAge(e.target.value)}
                    className="w-28 rounded-full border-0 bg-white px-3 py-1.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite selection:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Ethnicity
              </label>
              <div className="flex flex-wrap gap-2.5">
                {ethnicityOptions.map((item) =>
                  renderButton(item, selectedEthnicity, setSelectedEthnicity)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Complexion
              </label>
              <div className="flex flex-wrap gap-2.5">
                {complexionOptions.map((item) =>
                  renderButton(item, selectedComplexion, setSelectedComplexion)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Hair Style
              </label>
              <div className="flex flex-wrap gap-2.5">
                {getOptions(hairStyleOptions).map((item) =>
                  renderButton(item, selectedHairStyle, setSelectedHairStyle)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Face Shape
              </label>
              <div className="flex flex-wrap gap-2.5">
                {getOptions(faceShapeOptions).map((item) =>
                  renderButton(item, selectedFaceShape, setSelectedFaceShape)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Eye Type
              </label>
              <div className="flex flex-wrap gap-2.5">
                {eyeTypeOptions.map((item) =>
                  renderButton(item, selectedEyeType, setSelectedEyeType)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Eye Color
              </label>
              <div className="flex flex-wrap gap-2.5">
                {eyeColorOptions.map((item) =>
                  renderButton(item, selectedEyeColor, setSelectedEyeColor)
                )}
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="font-Mulish text-base font-semibold text-secondary block mb-2.5">
                Facial Expression
              </label>
              <div className="flex flex-wrap gap-2.5">
                {expressionOptions.map((item) =>
                  renderButton(item, selectedExpression, setSelectedExpression)
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border-l-2 border-primary-500 bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)]">
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handlePromptSubmit}
              disabled={loading || !isFormComplete}
              className={`rounded-full px-4 py-2 font-Mulish text-sm font-semibold ${
                loading || !isFormComplete
                  ? "cursor-not-allowed bg-primary-100 text-primary-800"
                  : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
              }`}
            >
              {loading ? <div className="flex items-center justify-center"> 
                  <span className="mr-2">Generating...</span> 
                  <Spinner width="4" height="4" fill="fill-primary-800" /> 
              </div> : `Generate Prompt (${PROMPT_CREDITS} credit)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateFashionModelPromptBuilder;
